import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useFocusTrap } from "@/lib/a11y/useFocusTrap";
import { motion } from "framer-motion";
import { PrimaryButton } from "@/components/Buttons/PrimaryButton";
import { SecondaryButton } from "@/components/Buttons/SecondaryButton";
import { useTranslations } from "next-intl";

type PConfirmDialogProps = {
  title: string;
  message: string;
  confirmText: string;
  /** Defaults to the localized "cancel" from the pAdditionalField namespace. */
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  /** Raise above a high-z host modal (e.g. the elevated field-settings modal). */
  elevated?: boolean;
  /**
   * A write is in flight. Spins the confirm button and shuts every dismissal
   * path — Escape, backdrop, Cancel. Dismissing mid-write would leave the user
   * looking at a list that has not caught up with a request they can no longer
   * see, which is exactly the state the confirm existed to prevent.
   */
  isLoading?: boolean;
  /** Extra content under the message — an opt-in checkbox, a warning line. */
  children?: React.ReactNode;
  /**
   * The message can change while the dialog is open — the company removal
   * swaps to the offboarding preview's sentence once it lands. Wraps the
   * message and `messageDetail` in ONE polite, atomic live region that exists
   * from the first frame, so a screen reader hears the new text (busy while
   * `confirmDisabled`). `children` stay outside it: a refusal's own alert must
   * not re-read the sentence. Absent = today's markup.
   */
  liveMessage?: boolean;
  /** A line that belongs to the message (inside the live region when there is one). */
  messageDetail?: React.ReactNode;
  /**
   * Hold the confirm button disabled on top of `isLoading` (the text it
   * confirms is still being settled). Absent = today's behaviour.
   */
  confirmDisabled?: boolean;
};

/**
 * Minimal, app-generic soft-confirm dialog (title + message + confirm/cancel).
 * Modeled on builders/portal/PortalConfirmDialog but decoupled from any one
 * i18n namespace — callers pass their own strings. Rendered through a portal,
 * closes on Escape or backdrop click. Use for lightweight "are you sure?"
 * gates; for typed-name destructive confirms use PDeleteConfirmation instead.
 */
export const PConfirmDialog: React.FC<PConfirmDialogProps> = ({
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  elevated = false,
  isLoading = false,
  children,
  liveMessage = false,
  messageDetail,
  confirmDisabled = false,
}) => {
  const t = useTranslations("pAdditionalField");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        if (isLoading) return;
        onCancel();
      }
    };
    // Capture phase so we win over a host modal's own Escape handler.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onCancel, isLoading]);

  // Focus lives INSIDE the dialog while it is open, and goes back to the opener
  // when it closes.
  //
  // This was attempted on 2026-08-12 and reverted, and the reason it failed is
  // worth keeping: that version focused the panel from a callback ref, which
  // runs in the commit phase — BEFORE the effect that captures
  // `document.activeElement` — so the "opener" it captured was the panel
  // itself, and the restore went to <body>. `useFocusTrap` captures first
  // (`useFocusTrap.ts:32`) and only then moves focus (`:43`), so that ordering
  // bug cannot occur here.
  //
  // Why it matters beyond a11y: `aria-modal="true"` already told screen readers
  // that nothing outside this panel exists, while focus sat outside it — on the
  // very button that opened the dialog. And Tab walked into the page behind,
  // whose table cells are `<Link>`s, so Enter navigated away with the question
  // still on screen. That is the keyboard twin of the click-through this dialog
  // stops with `stopPropagation`.
  //
  // No `initialFocusRef`: the first focusable in the panel is Cancel, which is
  // the right default for a confirm — the harmless answer is the one under the
  // user's finger.
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, true);

  return createPortal(
    <motion.div
      // Marker for host dialogs that own a window-level Escape (HubDialogShell):
      // this confirm portals to <body> and deliberately does NOT move focus, so
      // the focused element is still inside the host. Without the marker the
      // host would close itself on the very Escape meant to dismiss THIS
      // dialog — tearing down the form behind the question being asked.
      data-nested-dialog="true"
      className={`fixed inset-0 flex items-center justify-center bg-black/50 px-4 sm:pr-[calc(1rem+var(--ai-rail-push))] transition-[padding] duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none ${elevated ? "z-[10060]" : "z-[100]"}`}
      onClick={(e) => {
        // A portal escapes the DOM but NOT the React tree: every click in here
        // still bubbles to whatever rendered the opener. Callers sit inside
        // table rows whose own onClick opens a detail drawer, so clicking the
        // dialog's text, a checkbox label or the backdrop used to answer the
        // question AND open a panel behind it. Nothing outside a modal has any
        // business seeing these clicks.
        e.stopPropagation();
        if (isLoading) return;
        if (e.target === e.currentTarget) onCancel();
      }}
      // Same reason, for hosts that act on press rather than click (row
      // selection, drag starts) — those never got a guard at all.
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      role="presentation"
    >
      <motion.div
        ref={panelRef}
        className="w-full max-w-100 surface-primary rounded-2xl shadow-xl p-6 flex flex-col gap-4 outline-none"
        role="alertdialog"
        data-testid="confirm-dialog"
        aria-modal="true"
        aria-label={title}
        // The trap falls back to focusing the container when it finds nothing
        // focusable inside (which is what happens in jsdom, where every element
        // reports `offsetParent === null`). Without this the fallback silently
        // does nothing.
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", damping: 20, stiffness: 300 }}
      >
        <div className="flex flex-col gap-1">
          <p className="h3">{title}</p>
          {liveMessage ? (
            <div
              className="flex flex-col gap-1"
              aria-live="polite"
              aria-atomic="true"
              aria-busy={confirmDisabled ? "true" : undefined}
              data-testid="confirm-dialog-live"
            >
              <p className="body-3-regular font-secondary wrap-anywhere">{message}</p>
              {messageDetail}
            </div>
          ) : (
            <>
              {/* A message may carry an address — one unbroken word wider than a 375 px panel. */}
              <p className="body-3-regular font-secondary wrap-anywhere">{message}</p>
              {messageDetail}
            </>
          )}
        </div>
        {children}
        <div className="flex items-center gap-2">
          <SecondaryButton
            size="small"
            width="w-full"
            text={cancelText ?? t("cancel")}
            testId="confirm-dialog-cancel"
            disabled={isLoading}
            onClick={onCancel}
          />
          <PrimaryButton
            size="small"
            width="w-full"
            text={confirmText}
            testId="confirm-dialog-ok"
            loading={isLoading}
            disabled={isLoading || confirmDisabled}
            onClick={onConfirm}
          />
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
};
